import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import {
  addDotPhrase,
  deleteDotPhrase,
  fetchDotPhrases,
  updateDotPhrase,
} from "../api/dotphrases";
import { useLanguage } from "../i18n/LanguageContext";
import { t } from "../i18n";

const emptyForm = { abbreviation: "", text: "" };

export const DotPhrasesPage = () => {
  const { portal } = useLanguage();
  const copy = portal.dotphrases;
  const common = portal.common;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingTimesUsed, setEditingTimesUsed] = useState(0);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await fetchDotPhrases();
      setItems(Array.isArray(next) ? next : []);
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const ordered = [...items].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
    if (!query) return ordered;
    return ordered.filter((item) => {
      const abbr = (item.abbreviation || "").toLowerCase();
      const text = (item.text || "").toLowerCase();
      return abbr.includes(query) || text.includes(query);
    });
  }, [items, search]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setEditingTimesUsed(0);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setEditingTimesUsed(item.times_used ?? 0);
    setForm({
      abbreviation: item.abbreviation || "",
      text: item.text || "",
    });
    setSuccess("");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const abbreviation = form.abbreviation.trim().replace(/^\./, "");
    const text = form.text.trim();
    if (!abbreviation || !text) return;
    if (abbreviation.length < 2) {
      setError(copy.abbrTooShort);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (editingId) {
        await updateDotPhrase(editingId, {
          abbreviation,
          text,
          times_used: editingTimesUsed,
        });
        setSuccess(copy.updated);
      } else {
        await addDotPhrase({ abbreviation, text });
        setSuccess(copy.added);
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      t(copy.confirmDelete, { abbr: item.abbreviation, text: item.text })
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    try {
      await deleteDotPhrase(item._id);
      if (editingId === item._id) resetForm();
      setSuccess(copy.deleted);
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteFailed);
    }
  };

  return (
    <AppShell title={copy.title} description={copy.description}>
      {error && <div className="app-alert">{error}</div>}
      {success && <div className="app-alert success">{success}</div>}

      <section className="app-form-card">
        <h2>{editingId ? copy.editTitle : copy.addTitle}</h2>
        <form onSubmit={handleSubmit} className="app-form">
          <label htmlFor="dot-abbr">{copy.abbrLabel}</label>
          <input
            id="dot-abbr"
            type="text"
            value={form.abbreviation}
            onChange={(e) =>
              setForm((current) => ({
                ...current,
                abbreviation: e.target.value,
              }))
            }
            placeholder={copy.abbrPlaceholder}
            required
            disabled={saving || Boolean(editingId)}
          />
          {editingId && <p className="app-hint">{copy.abbrLocked}</p>}

          <label htmlFor="dot-text">{copy.textLabel}</label>
          <textarea
            id="dot-text"
            rows={4}
            value={form.text}
            onChange={(e) =>
              setForm((current) => ({ ...current, text: e.target.value }))
            }
            placeholder={copy.textPlaceholder}
            required
            disabled={saving}
          />

          <div className="app-form-actions">
            <button type="submit" className="app-primary" disabled={saving}>
              {saving
                ? common.saving
                : editingId
                  ? copy.update
                  : copy.add}
            </button>
            {editingId && (
              <button
                type="button"
                className="app-secondary"
                onClick={resetForm}
                disabled={saving}
              >
                {common.cancel}
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="app-table-card">
        <div className="app-toolbar">
          <input
            type="search"
            className="app-search"
            placeholder={copy.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={copy.searchAria}
          />
          <span className="app-count">
            {loading
              ? common.loading
              : `${filtered.length} ${common.of} ${items.length}`}
          </span>
        </div>

        <div className="app-table-wrap">
          <table className="app-table">
            <thead>
              <tr>
                <th>{copy.colAbbr}</th>
                <th>{copy.colText}</th>
                <th>{copy.colUsed}</th>
                <th>{copy.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="app-empty">
                    {copy.empty}
                  </td>
                </tr>
              )}
              {filtered.map((item) => (
                <tr key={item._id}>
                  <td>.{item.abbreviation}</td>
                  <td className="app-text-cell">{item.text}</td>
                  <td>{item.times_used ?? 0}</td>
                  <td className="app-actions-cell">
                    <button
                      type="button"
                      className="app-link-btn"
                      onClick={() => handleEdit(item)}
                    >
                      {common.edit}
                    </button>
                    <button
                      type="button"
                      className="app-link-btn danger"
                      onClick={() => handleDelete(item)}
                    >
                      {common.delete}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AppShell>
  );
};
